import { useEffect, useState } from 'react';
import { RefreshCw, UserX, Eye } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import { Button } from '../../../components/ui/button';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import StatusChip from '../../../components/common/StatusChip';
import ErrorState from '../../../components/common/ErrorState';
import Drawer from '../../../components/common/Drawer';
import LoadingSkeleton from '../../../components/common/LoadingSkeleton';
import ConfirmationModal from '../../../components/common/ConfirmationModal';
import { mamClientAccountService } from '../services/mamService';
import { formatCurrency, formatDate } from '../utils';
import type { MAMClientAccount } from '../types';
import { Tooltip } from '../../../components/ui/tooltip';

export default function MAMClientAccounts() {
  const [clients, setClients] = useState<MAMClientAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<MAMClientAccount | null>(
    null
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [disconnectModal, setDisconnectModal] = useState<{
    isOpen: boolean;
    clientId: string;
    clientName: string;
    loading?: boolean;
  }>({
    isOpen: false,
    clientId: '',
    clientName: '',
  });

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mamClientAccountService.getAll();
      setClients(data);
    } catch {
      setError('Failed to fetch client sub-accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const confirmDisconnect = async () => {
    setDisconnectModal(prev => ({ ...prev, loading: true }));
    try {
      await mamClientAccountService.disconnect(disconnectModal.clientId);
      setClients(prev =>
        prev.map(c =>
          c.id === disconnectModal.clientId
            ? { ...c, status: 'disconnected' }
            : c
        )
      );
    } finally {
      setDisconnectModal({ isOpen: false, clientId: '', clientName: '' });
    }
  };

  const columns: DataTableColumn<MAMClientAccount>[] = [
    {
      id: 'clientName',
      header: 'Client / Account',
      sortable: true,
      accessor: cli => (
        <div>
          <p className="font-bold text-foreground text-xs">{cli.clientName}</p>
          <p className="text-[10px] text-muted-foreground">
            {cli.accountNumber} • {cli.email}
          </p>
        </div>
      ),
    },
    {
      id: 'groupName',
      header: 'Assigned Group',
      sortable: true,
      accessor: cli => (
        <div>
          <p className="font-semibold text-foreground text-xs">
            {cli.groupName}
          </p>
          <p className="text-[10px] text-muted-foreground">
            Manager: {cli.managerName}
          </p>
        </div>
      ),
    },
    {
      id: 'equity',
      header: 'Equity',
      sortable: true,
      align: 'right',
      accessor: cli => (
        <span className="font-bold text-foreground">
          {formatCurrency(cli.equity)}
        </span>
      ),
    },
    {
      id: 'balance',
      header: 'Balance',
      sortable: true,
      align: 'right',
      accessor: cli => <span>{formatCurrency(cli.balance)}</span>,
    },
    {
      id: 'allocatedLots',
      header: 'Allocated Lots',
      sortable: true,
      align: 'right',
      accessor: cli => (
        <span className="font-mono font-bold text-indigo-600">
          {cli.allocatedLots}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      align: 'center',
      accessor: cli => <StatusChip status={cli.status} size="sm" />,
    },
    {
      id: 'actions',
      header: 'Actions',
      align: 'right',
      accessor: cli => (
        <div className="flex items-center justify-end space-x-1">
          <Tooltip content="view">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={e => {
                e.stopPropagation();
                setSelectedClient(cli);
                setDrawerOpen(true);
              }}
            >
              <Eye className="w-3.5 h-3.5" />
            </Button>
          </Tooltip>
          {cli.status === 'active' && (
            <Tooltip content="Disconnect">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-rose-600 hover:text-rose-700"
                onClick={e => {
                  e.stopPropagation();
                  setDisconnectModal({
                    isOpen: true,
                    clientId: cli.id,
                    clientName: cli.clientName,
                  });
                }}
              >
                <UserX className="w-3.5 h-3.5" />
              </Button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="MAM Client Accounts" />
        <LoadingSkeleton type="table" rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="MAM Client Accounts" />
        <ErrorState message={error} onRetry={loadClients} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6 w-full">
      <PageHeader
        title="MAM Client Accounts"
        description="Sub-accounts attached to master allocation groups"
      >
        <Button variant="outline" size="sm" onClick={loadClients}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </PageHeader>

      <DataTable
        columns={columns}
        data={clients}
        searchKeys={['clientName', 'email', 'accountNumber', 'groupName']}
        searchPlaceholder="Search sub-accounts by client name, email, account #..."
        onRowClick={cli => {
          setSelectedClient(cli);
          setDrawerOpen(true);
        }}
        initialPageSize={8}
      />

      <Drawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedClient?.clientName || 'Client Sub-Account'}
        subtitle={`Account #${selectedClient?.accountNumber}`}
      >
        {selectedClient && (
          <div className="space-y-4 text-xs">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <p className="font-semibold text-foreground">
                  {selectedClient.clientName}
                </p>
                <p className="text-muted-foreground">{selectedClient.email}</p>
              </div>
              <StatusChip status={selectedClient.status} size="sm" />
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="p-3 bg-card border border-border rounded-lg">
                <p className="text-muted-foreground text-[11px]">
                  Assigned Group
                </p>
                <p className="font-bold text-foreground text-sm">
                  {selectedClient.groupName}
                </p>
              </div>
              <div className="p-3 bg-card border border-border rounded-lg">
                <p className="text-muted-foreground text-[11px]">
                  Master Manager
                </p>
                <p className="font-bold text-foreground text-sm">
                  {selectedClient.managerName}
                </p>
              </div>
              <div className="p-3 bg-card border border-border rounded-lg">
                <p className="text-muted-foreground text-[11px]">Equity</p>
                <p className="font-bold text-foreground text-sm">
                  {formatCurrency(selectedClient.equity)}
                </p>
              </div>
              <div className="p-3 bg-card border border-border rounded-lg">
                <p className="text-muted-foreground text-[11px]">Balance</p>
                <p className="font-bold text-foreground text-sm">
                  {formatCurrency(selectedClient.balance)}
                </p>
              </div>
            </div>

            <div className="pt-2">
              <p className="text-muted-foreground text-[11px]">Joined Date</p>
              <p className="font-semibold text-foreground">
                {formatDate(selectedClient.joinedDate)}
              </p>
            </div>
          </div>
        )}
      </Drawer>

      <ConfirmationModal
        isOpen={disconnectModal.isOpen}
        title="Disconnect Client Account"
        message={`Are you sure you want to disconnect ${disconnectModal.clientName} from the MAM group?`}
        type="danger"
        confirmText="Disconnect Account"
        cancelText="Cancel"
        loading={disconnectModal.loading}
        onConfirm={confirmDisconnect}
        onCancel={() =>
          setDisconnectModal({ isOpen: false, clientId: '', clientName: '' })
        }
      />
    </div>
  );
}
